import { requisitar } from '@/lib/http';
import type {
  AreaNaLista,
  ClienteNaLista,
  PerfilCodigo,
  TipoArea,
  UsuarioCriado,
  UsuarioNaLista,
} from '@/types/api';

/**
 * Endpoints de administracao de cadastros.
 *
 * INATIVAR E DESATIVAR SAO `POST`, nao `DELETE`. Nao e exclusao: a linha permanece
 * porque acao e historico apontam para ela.
 */

// --- Areas ----------------------------------------------------------------

export interface DadosDeArea {
  nome: string;
  tipo: TipoArea;
  ordem: number;
}

export function listarAreas(sinal?: AbortSignal): Promise<AreaNaLista[]> {
  return requisitar<AreaNaLista[]>('/api/areas', sinal === undefined ? {} : { sinal });
}

export function criarArea(dados: DadosDeArea): Promise<AreaNaLista> {
  return requisitar<AreaNaLista>('/api/areas', { metodo: 'POST', corpo: dados });
}

export function alterarArea(id: number, dados: DadosDeArea): Promise<AreaNaLista> {
  return requisitar<AreaNaLista>(`/api/areas/${String(id)}`, { metodo: 'PUT', corpo: dados });
}

export function alternarArea(id: number, ativar: boolean): Promise<AreaNaLista> {
  const rota = ativar ? 'reativar' : 'inativar';

  return requisitar<AreaNaLista>(`/api/areas/${String(id)}/${rota}`, { metodo: 'POST' });
}

// --- Clientes -------------------------------------------------------------

export function listarClientes(texto?: string, sinal?: AbortSignal): Promise<ClienteNaLista[]> {
  const busca = texto?.trim();
  const consulta = busca === undefined || busca === '' ? '' : `?texto=${encodeURIComponent(busca)}`;

  return requisitar<ClienteNaLista[]>(
    `/api/clientes${consulta}`,
    sinal === undefined ? {} : { sinal },
  );
}

export function criarCliente(nome: string): Promise<ClienteNaLista> {
  return requisitar<ClienteNaLista>('/api/clientes', { metodo: 'POST', corpo: { nome } });
}

export function alterarCliente(id: number, nome: string): Promise<ClienteNaLista> {
  return requisitar<ClienteNaLista>(`/api/clientes/${String(id)}`, {
    metodo: 'PUT',
    corpo: { nome },
  });
}

export function alternarCliente(id: number, ativar: boolean): Promise<ClienteNaLista> {
  const rota = ativar ? 'reativar' : 'inativar';

  return requisitar<ClienteNaLista>(`/api/clientes/${String(id)}/${rota}`, { metodo: 'POST' });
}

// --- Usuarios -------------------------------------------------------------

export interface DadosDeUsuario {
  nome: string;
  email: string;
  perfil: PerfilCodigo;
  areas: number[];
}

export function listarUsuarios(sinal?: AbortSignal): Promise<UsuarioNaLista[]> {
  return requisitar<UsuarioNaLista[]>('/api/usuarios', sinal === undefined ? {} : { sinal });
}

/** Cria o usuario. A resposta traz a senha temporaria, uma unica vez. */
export function criarUsuario(dados: DadosDeUsuario): Promise<UsuarioCriado> {
  return requisitar<UsuarioCriado>('/api/usuarios', { metodo: 'POST', corpo: dados });
}

export function alterarUsuario(id: number, dados: DadosDeUsuario): Promise<UsuarioNaLista> {
  return requisitar<UsuarioNaLista>(`/api/usuarios/${String(id)}`, {
    metodo: 'PUT',
    corpo: dados,
  });
}

/** Desativar revoga as sessoes do usuario na mesma gravacao, no servidor. */
export function alternarUsuario(id: number, ativar: boolean): Promise<UsuarioNaLista> {
  const rota = ativar ? 'reativar' : 'desativar';

  return requisitar<UsuarioNaLista>(`/api/usuarios/${String(id)}/${rota}`, { metodo: 'POST' });
}

/** Gera senha temporaria, exige troca no proximo acesso e revoga as sessoes. */
export function redefinirSenha(id: number): Promise<UsuarioCriado> {
  return requisitar<UsuarioCriado>(`/api/usuarios/${String(id)}/redefinir-senha`, {
    metodo: 'POST',
  });
}
